import { useState, type Ref } from 'react';
import { CaretLeft } from '@hungryroot/icons';
import { IngredientsImageGrid } from '@hungryroot/pho';
import { Button, Heading } from '@hungryroot/ramen';
import { type PairingV1 as Pairing } from '@hungryroot/rest';
import { type CustomerRatableProduct as RatableProduct } from '@hungryroot/rest/api/v2/customers/[id]/ratable_products_feedback_modal/get';
import { type RatablePairing } from '../../../hooks/useSelfRatablePairings';
import {
  type UserDefinedRating,
  type LikeDislikeRatings,
  ApiV1LikeDislikeEnum as LikeDislikeEnum,
} from '../BinaryRatings/api';
import {
  LikeDislike,
  LikeDislikeSize,
  LikeDislikeVariant,
} from '../LikeDislike/LikeDislike';
import styles from './RatingSheet.module.css';
interface RatingModalProps {
  showBackButton?: boolean;
  onBackButtonClick?: () => void;
  onSubmitButtonClick?: (feedback?: string) => void;
  isPairing?: boolean;
}

type Props = {
  testid?: string;
  rating: UserDefinedRating;
  pairing:
    | Pick<Pairing, 'name' | 'ingredients'>
    | Pick<RatablePairing, 'name' | 'ingredients'>
    | RatableProduct;
  textareaRef?: Ref<HTMLTextAreaElement>;
  onRating: (rating: UserDefinedRating) => void;
  onFeedbackSubmit: (params: { feedback: string }) => void;
  feedback?: string;
  bodyClassName?: string;
} & RatingModalProps;

export function RatingSheet(props: Props) {
  const {
    testid,
    rating,
    pairing,
    textareaRef,
    onRating,
    onFeedbackSubmit,
    showBackButton,
    onBackButtonClick,
    onSubmitButtonClick,
    feedback,
    bodyClassName,
    isPairing = true,
  } = props;
  const [feedbackText, setFeedbackText] = useState<string>(feedback || '');
  const { name: pairingName } = pairing;
  const title = `How was the ${pairingName}?`;
  const getButtonOnClick = () => {
    if (!showBackButton && onSubmitButtonClick) {
      return onSubmitButtonClick(feedbackText);
    }
    return onFeedbackSubmit({
      feedback: feedbackText,
    });
  };

  const ingredients =
    'ingredients' in pairing ? pairing.ingredients : [pairing];

  const getProductImg = (product: RatableProduct) => {
    const { packaged_image_url: img, name: productName } = product;
    return (
      img && (
        <img
          src={img}
          data-testid={`${testid}-featured-img`}
          className={styles['product-img']}
          alt={productName}
        />
      )
    );
  };

  const placeholderText =
    rating === LikeDislikeEnum.Disliked
      ? 'I didn’t like the taste of…'
      : 'I liked the taste of…';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onFeedbackSubmit({
          feedback: feedbackText,
        });
      }}
      className={styles.container}
      data-testid={testid}
    >
      <section
        className={styles['images-container']}
        data-testid={`${testid}-images`}
      >
        {isPairing ? (
          <IngredientsImageGrid
            testid={`${testid}-image-grid`}
            ingredients={ingredients as Pairing['ingredients']}
          />
        ) : (
          getProductImg(pairing as RatableProduct)
        )}
      </section>
      <section className={`${styles.body} ${bodyClassName}`}>
        <div className={styles.content}>
          {showBackButton && onBackButtonClick && (
            <button
              onClick={onBackButtonClick}
              data-testid={`${testid}-back-button`}
              className={styles['back-button']}
            >
              <CaretLeft />
            </button>
          )}
          <Heading testid={`${testid}-title`} variant="H2">
            {title}
          </Heading>
          <LikeDislike
            className={styles['like-dislike']}
            rating={rating}
            size={LikeDislikeSize.Large}
            onRating={(clickedRating) => {
              if (
                clickedRating !== rating &&
                clickedRating !== LikeDislikeEnum.NoRating
              ) {
                onRating(clickedRating as LikeDislikeRatings);
              }
            }}
            testid={`${testid}-like-dislike`}
            variant={LikeDislikeVariant.Pairing}
          />
          <Heading testid={`${testid}-share-feedback-title`} variant="H6">
            Your feedback helps us improve your deliveries.
          </Heading>
          <textarea
            ref={textareaRef}
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder={placeholderText}
            data-testid={`${testid}-share-textarea`}
            className={styles.textarea}
          ></textarea>
        </div>
        <footer className={styles.footer}>
          <Button
            width="full-width"
            size="medium"
            testid={`${testid}-button-submit`}
            buttonType="submit"
            prominence="primary"
            style={onSubmitButtonClick !== undefined ? 'default' : 'muted'}
            textPrimary={
              showBackButton && onSubmitButtonClick !== undefined
                ? 'Continue'
                : 'Submit'
            }
            onClick={() => getButtonOnClick()}
          />
        </footer>
      </section>
    </form>
  );
}
